import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { SbTable, SbNotification } from "uiCore";
import {
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
} from "styles/common.styles";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { ClickableTableWrapperStyled } from "../sharedTable.styles";
import { PageTransitionStyled } from "../talentMobility.animations";
import { useTalentMobilityEmployees } from "../useTalentMobilityData";
import { groupTableRowsByRole } from "../talentMobilityEmployees.utils";
import { TEXT } from "../talentMobility.constants";
import { PLANNING_TOOLS_MENU_ITEMS, PLANNING_TOOLS_ROUTES } from "./TalentMobilityManagerV2.constants";
import { HeaderActionsRowStyled, PlanningToolsTriggerStyled } from "./TalentMobilityManagerV2.styles";
import useTalentMobilityManagerColumns from "./useTalentMobilityManagerColumns";
import TalentMobilityRoleEmployeesTable from "./TalentMobilityRoleEmployeesTable";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

const ANALYTICS_FROM = "talent_mobility_manager";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.MANAGER_DASHBOARD_VIEWED;

const TalentMobilityManagerV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const { employees, updateRoleField, isLoading, error } = useTalentMobilityEmployees();
  const thresholds = useMatchThresholds();
  // everExpandedRowKeys only ever grows -- fed to antd's own
  // expandable.expandedRowKeys so a role's row, once opened, stays mounted
  // and displayed forever after (never flipped back to antd's display:
  // none). That's what leaves room for TalentMobilityRoleEmployeesTable's
  // own CSS transition to animate the close: antd's native toggle would
  // otherwise snap a collapsing row to display: none the instant its key
  // left this list, with no time for any transition to play. openRowKeys is
  // the real open/closed state, toggled freely per click and passed down as
  // isOpen to actually drive that animation.
  const [everExpandedRowKeys, setEverExpandedRowKeys] = useState([]);
  const [openRowKeys, setOpenRowKeys] = useState([]);

  useEffect(() => {
    if (!error) return;
    SbNotification.error(
      "Unable to load employees",
      error?.response?.data?.message || "Please refresh the page to try again."
    );
  }, [error]);

  // One row per role (jd_sb_unique_id) — recomputed whenever an employee's
  // Open/Discoverable checkbox changes (updateRoleField updates `employees`),
  // so a role's counts and its expanded sub-table stay in sync automatically.
  const roles = useMemo(() => groupTableRowsByRole(employees), [employees]);
  const columns = useTalentMobilityManagerColumns({ roles, thresholds, navigate });

  const toggleExpandedRow = useCallback((rowKey) => {
    setOpenRowKeys((previous) =>
      previous.includes(rowKey) ? previous.filter((key) => key !== rowKey) : [...previous, rowKey]
    );
    setEverExpandedRowKeys((previous) => (previous.includes(rowKey) ? previous : [...previous, rowKey]));
  }, []);

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={4}>{TEXT.PAGE_TITLE}</Title>
          </HeadingRowStyled>
          {TEXT.PAGE_DESCRIPTION.map((line) => (
            <Text type="secondary" style={{ display: "block" }} key={line}>
              {line}
            </Text>
          ))}
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <HeaderActionsRowStyled>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: PLANNING_TOOLS_MENU_ITEMS,
                onClick: ({ key }) => navigate(PLANNING_TOOLS_ROUTES[key]),
              }}
            >
              <PlanningToolsTriggerStyled>
                {TEXT.BTN_PLANNING_TOOLS} <DownOutlined />
              </PlanningToolsTriggerStyled>
            </Dropdown>
          </HeaderActionsRowStyled>
        </HeaderRightStyled>
      </PageHeaderStyled>

      <ClickableTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={roles}
          loading={isLoading}
          rowKey="id"
          sticky
          onRow={(record) => ({ onClick: () => toggleExpandedRow(record.id) })}
          expandable={{
            expandedRowKeys: everExpandedRowKeys,
            showExpandColumn: false,
            rowExpandable: () => true,
            // Unconditional, not just while closed -- antd's own cell
            // padding around expandedRowRender's content would otherwise
            // still show as a fixed-height empty gap even at
            // grid-template-rows: 0fr (see sharedTable.styles.js), since
            // that padding lives one level above what the animation
            // collapses. NestedTableWrapperStyled already supplies the
            // panel's real padding, so this is pure redundant space either way.
            expandedRowClassName: () => "tm-role-panel-cell",
            expandedRowRender: (record) => (
              <TalentMobilityRoleEmployeesTable
                role={record}
                updateRoleField={updateRoleField}
                isOpen={openRowKeys.includes(record.id)}
              />
            ),
          }}
        />
      </ClickableTableWrapperStyled>
    </PageTransitionStyled>
  );
};

export default TalentMobilityManagerV2;
